import type { IInputProps } from './Input'

import { styled } from '@linaria/react'
import { ActionButtonSC } from 'atoms/ActionButton'
import { INDENTS, MEDIA } from 'vars/index'
import { InputActionButtonPosition, InputBackgroundColors } from './Input.h'

export const InputWrapper = styled.div<
  Pick<IInputProps, 'actionButtonPosition' | 'actionButtonIndent'> & {
    inputWrapperWidth?: string
    inputWrapperHeight?: string
  }
>`
  width: ${({ inputWrapperWidth }) => inputWrapperWidth || 'auto'};
  height: ${({ inputWrapperHeight }) => inputWrapperHeight || 'auto'};
  max-width: 100%;
  position: relative;

  & ${ActionButtonSC} {
    position: absolute;
    left: ${({ actionButtonPosition, actionButtonIndent }) =>
      actionButtonPosition === InputActionButtonPosition.Left
        ? actionButtonIndent
          ? `var(${actionButtonIndent})`
          : 0
        : 'auto'};
    right: ${({ actionButtonPosition, actionButtonIndent }) =>
      actionButtonPosition === InputActionButtonPosition.Right
        ? actionButtonIndent
          ? `var(${actionButtonIndent})`
          : 0
        : 'auto'};
    top: 50%;
    transform: translate(0, -50%);
    cursor: pointer;
  }
`

export const InputSC = styled.input<
  IInputProps & {
    inputHeight?: string
  }
>`
  width: 100%;
  height: ${({ inputHeight }) => inputHeight || 'auto'};
  font: ${({ font }) => `var(${font})`};
  color: ${({ disabled, colorDisabled, colorDefault }) =>
    disabled
      ? `var(${colorDisabled})`
      : colorDefault
        ? `var(${colorDefault})`
        : 'inherit'};
  background-color: ${({ backgroundColorDefault }) =>
    `var(${backgroundColorDefault})`};
  border: none;
  /* TODO: redo borders, borders should apply for all element sides */
  border-bottom-width: ${({ borders }) => `var(${borders})`};
  border-bottom-color: ${({
    invalid,
    borderColorDefault,
    borderColorInvalid,
  }) =>
    invalid ? `var(${borderColorInvalid})` : `var(${borderColorDefault})`};
  border-bottom-style: solid;
  padding: ${({ paddings }) => {
    if (typeof paddings === 'string') {
      return `var(${paddings})`
    }

    return paddings && paddings.length > 0
      ? paddings.map((item) => `var(${item})`)?.join(' ')
      : 0
  }};
  padding-right: ${({ icon, secured, paddings, actionButtonPosition }) =>
    (secured && actionButtonPosition === InputActionButtonPosition.Right) ||
    (icon && actionButtonPosition === InputActionButtonPosition.Right)
      ? paddings?.[1] === INDENTS.INDENT_120
        ? `var(${INDENTS.INDENT_120})`
        : `var(${INDENTS.INDENT_140})`
      : `var(${paddings?.[1] || INDENTS.INDENT_60})`};
  border-radius: ${({ corners }) => `var(${corners})`};
  cursor: pointer;
  caret-color: ${({ caretColor }) => `var(${caretColor})`};

  &::placeholder {
    color: ${({ disabled, colorDisabled, placeholderColor }) =>
      disabled
        ? `var(${colorDisabled})`
        : placeholderColor
          ? `var(${placeholderColor})`
          : 'inherit'};
  }

  &:focus {
    background-color: ${({ backgroundColorPressed }) =>
      `var(${backgroundColorPressed})`};
    border-bottom-color: ${({ borderColorPressed }) =>
      `var(${borderColorPressed})`};
  }

  &:-webkit-autofill {
    transition: background-color 5000s ease-in-out 0s;
    -webkit-text-fill-color: ${({ disabled, colorDisabled, colorDefault }) =>
      disabled
        ? `var(${colorDisabled})`
        : colorDefault
          ? `var(${colorDefault})`
          : 'inherit'};
  }

  &[type='date'] {
    ::-webkit-inner-spin-button,
    ::-webkit-calendar-picker-indicator {
      display: none;
      -webkit-appearance: none;
    }
  }
`

export const InputLabel = styled.label<IInputProps>`
  display: flex;
  width: ${({ inputLabelWidth }) => inputLabelWidth || 'auto'};
  flex-direction: column;
  padding: ${({ inputLabelPaddings }) =>
    inputLabelPaddings && inputLabelPaddings.length > 0
      ? inputLabelPaddings.map((item) => `var(${item})`)?.join(' ')
      : 0};
  gap: ${({ labelGap }) => `var(${labelGap})`};

  ${MEDIA.NO_TOUCH} {
    &:hover ${InputSC} {
      background-color: var(${InputBackgroundColors.SURFACE_OPACITY_LIGHT_15});

      &:focus {
        background-color: ${({ backgroundColorPressed }) =>
          `var(${backgroundColorPressed})`};
        border-bottom-color: ${({ borderColorPressed }) =>
          `var(${borderColorPressed})`};
      }
    }
  }
`
